import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  SwapHoriz,
  DirectionsBoat,
  Notes,
  Download,
  Refresh,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Visibility,
  Delete,
  ArrowForward,
  UploadFile,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { selectKeyForRole, selectStatusOrder } from "../../redux/slices/statusDefinitionsSlice";

import useShipments from "../../hooks/useShipments";
import api from "../../api/axiosInstance";
import StatusChip from "../../components/common/StatusChip";
import ShipmentTracker from "../../components/common/ShipmentTracker";
import InvoiceDialog from "../../components/common/InvoiceDialog";
import ShipmentTimeline from "../../components/shipments/ShipmentTimeline";
import StatusUpdateDialog from "../../components/shipments/StatusUpdateDialog";
import ItemStatusUpdateDialog from "../../components/shipments/ItemStatusUpdateDialog";
import shipmentService from "../../services/shipmentService";
import AssignSailingDialog from "../../components/sailing/AssignSailingDialog";
import invoiceService from "../../services/invoiceService";
import documentService from "../../services/documentService";
import ConsolidationPickerDialog from "../../components/common/ConsolidationPickerDialog";
import { CallMerge } from "@mui/icons-material";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { formatUsd } from "../../utils/currency";

const AdminShipmentDetail = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemReceivedKey = useSelector(selectKeyForRole("item", "received"));
  const itemDeliveredKey = useSelector(selectKeyForRole("item", "delivered"));
  const itemStatusOrder = useSelector(selectStatusOrder("item"));
  const itemPendingKey = itemStatusOrder[0];
  const receivedIdx = itemStatusOrder.indexOf(itemReceivedKey);
  // Eligible for a consolidation only while still pending or received —
  // once it moves to scheduled_for_sailing (or anything after), it's
  // already committed to its own timeline.
  const isItemConsolidationEligible = (item) => {
    if (item.consolidation) return false;
    const idx = item.itemStatus ? itemStatusOrder.indexOf(item.itemStatus) : 0;
    return receivedIdx === -1 || idx <= receivedIdx;
  };
  const {
    current: shipment,
    loading,
    loadOne,
    update,
    changeStatus,
    syncTracking,
    remove,
  } = useShipments();

  const [tab, setTab] = useState(0);
  const [activityLog, setActivityLog] = useState([]);
  const [loadingActivityLog, setLoadingActivityLog] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [syncingItemId, setSyncingItemId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [assignSailingOpen, setAssignSailingOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [viewingDocId, setViewingDocId] = useState(null);
  const [uploadingDocType, setUploadingDocType] = useState(null); // "invoice" | "other" | null
  const [deletingDocId, setDeletingDocId] = useState(null);
  const invoiceFileInputRef = React.useRef(null);
  const otherFileInputRef = React.useRef(null);
  const [itemMeasurements, setItemMeasurements] = useState({});
  const [savingMeasurementsId, setSavingMeasurementsId] = useState(null);
  const [weightCbfDraft, setWeightCbfDraft] = useState({});
  const [savingWeightCbfId, setSavingWeightCbfId] = useState(null);
  const [feeDraft, setFeeDraft] = useState(null);
  const [savingFees, setSavingFees] = useState(false);
  const [updatingItemStatusId, setUpdatingItemStatusId] = useState(null);
  const [itemStatusDialogItem, setItemStatusDialogItem] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleItemExpanded = (itemId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const saveItemMeasurements = async (itemId) => {
    const draft = itemMeasurements[itemId];
    if (!draft) return;

    setSavingMeasurementsId(itemId);
    try {
      const updatedItems = shipment.items.map((it) =>
        it._id === itemId
          ? {
            ...it,
            unitValue: draft.unitValue !== undefined ? parseFloat(draft.unitValue) || 0 : it.unitValue,
          }
          : it,
      );
      await update(id, { items: updatedItems });
      enqueueSnackbar("Item measurements saved", { variant: "success" });
      loadOne(id);
    } catch (err) {
      enqueueSnackbar("Failed to save item measurements", { variant: "error" });
    } finally {
      setSavingMeasurementsId(null);
    }
  };

  // Manual weight/CBF override for one item — overrides its auto-computed
  // even split of the D/R's total; clearing the field(s) back to blank
  // sends null, which drops the override and lets that item rejoin the
  // auto-split with the rest of its D/R group.
  const saveWeightCbf = async (itemId) => {
    const draft = weightCbfDraft[itemId];
    if (!draft) return;

    setSavingWeightCbfId(itemId);
    try {
      const weight = draft.weight === "" || draft.weight == null ? null : parseFloat(draft.weight);
      const cbf = draft.cbf === "" || draft.cbf == null ? null : parseFloat(draft.cbf);
      await shipmentService.setItemMeasurements(id, itemId, { weight, cbf });
      enqueueSnackbar("Weight/CBF saved", { variant: "success" });
      setWeightCbfDraft((p) => {
        const next = { ...p };
        delete next[itemId];
        return next;
      });
      loadOne(id);
    } catch (err) {
      enqueueSnackbar("Failed to save weight/CBF", { variant: "error" });
    } finally {
      setSavingWeightCbfId(null);
    }
  };

  const saveFees = async () => {
    if (!feeDraft) return;
    setSavingFees(true);
    try {
      await update(id, {
        deliveryFee: parseFloat(feeDraft.deliveryFee) || 0,
        handlingFee: parseFloat(feeDraft.handlingFee) || 0,
        storageFee: parseFloat(feeDraft.storageFee) || 0,
      });
      enqueueSnackbar("Shipment fees saved", { variant: "success" });
      setFeeDraft(null);
      loadOne(id);
    } catch (err) {
      enqueueSnackbar("Failed to save shipment fees", { variant: "error" });
    } finally {
      setSavingFees(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete shipment ${shipment.trackingNumber}? This cannot be undone.`)) return;
    const ok = await remove(id);
    if (ok) navigate("/admin/shipments");
  };

  const handleItemStatusChange = async (status, note, notifyCustomer) => {
    const item = itemStatusDialogItem;
    if (!item) return;
    setUpdatingItemStatusId(item._id);
    try {
      const { data } = await shipmentService.updateItemStatus(
        id,
        item._id,
        status,
        note,
        notifyCustomer,
      );
      enqueueSnackbar(data.message, { variant: "success" });
      setItemStatusDialogItem(null);
      loadOne(id);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update item status", {
        variant: "error",
      });
    } finally {
      setUpdatingItemStatusId(null);
    }
  };

  const toggleDeliverySelected = async (e) => {
    const deliverySelected = e.target.checked;
    try {
      await update(id, { deliverySelected });
      loadOne(id);
    } catch (err) {
      enqueueSnackbar("Failed to update delivery selection", { variant: "error" });
    }
  };

  useEffect(() => {
    if (id) loadOne(id);
  }, [id]);

  useEffect(() => {
    if (shipment) setInternalNote(shipment.internalNotes || "");
  }, [shipment]);

  // Deep-link from the Shipments list search: clicking a result that
  // matched on an ITEM's carrier tracking number (rather than the
  // shipment's own) lands here with ?item=<itemId> — jump to the Items
  // tab, expand that item, and scroll it into view instead of leaving the
  // admin to hunt for it manually.
  useEffect(() => {
    if (!shipment) return;
    const itemId = searchParams.get("item");
    if (!itemId) return;

    setTab(1);
    setExpandedItems((prev) => new Set(prev).add(itemId));
    requestAnimationFrame(() => {
      const el = document.getElementById(`shipment-item-${itemId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    // Drop the param so it doesn't keep re-triggering (e.g. after an item
    // edit re-fetches the shipment and this effect runs again).
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("item");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment?._id]);

  // Loaded on demand, only when the admin actually opens the Edit History
  // tab — no point fetching it on every shipment-detail page view.
  useEffect(() => {
    if (tab !== 6 || !shipment?._id) return;
    setLoadingActivityLog(true);
    shipmentService
      .getActivityLog(shipment._id)
      .then(({ data }) => setActivityLog(data.data?.entries || []))
      .catch(() => enqueueSnackbar("Failed to load edit history", { variant: "error" }))
      .finally(() => setLoadingActivityLog(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, shipment?._id]);

  const handleStatusUpdate = async (status, note, notifyCustomer) => {
    setUpdatingStatus(true);
    await changeStatus(id, status, note, notifyCustomer);
    setUpdatingStatus(false);
    setStatusOpen(false);
    loadOne(id);
  };

  const saveNote = async () => {
    setSavingNote(true);
    await update(id, { internalNotes: internalNote });
    setSavingNote(false);
  };

  const handleSyncItem = async (itemId) => {
    setSyncingItemId(itemId);
    try {
      await api.post(`/shipments/${id}/items/${itemId}/sync`);
      enqueueSnackbar("Item tracking updated", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Failed to update item tracking",
        { variant: "error" },
      );
    }
    setSyncingItemId(null);
    loadOne(id);
  };

  const handleSyncAllItems = async () => {
    setSyncingAll(true);
    const result = await syncTracking(id);
    setSyncingAll(false);
    if (result) {
      loadOne(id);
    }
  };

  // Consolidation picker — opened for either the whole shipment
  // ("Request Consolidation") or a single item ("Add to Consolidation"),
  // never auto-merged silently; the dialog lets the admin explicitly pick
  // an existing open consolidation belonging to this shipment's customer,
  // or start a new one.
  const [consolidationPicker, setConsolidationPicker] = useState(null); // { mode, itemId? }

  const handleCreateInvoice = async (form) => {
    setCreatingInvoice(true);
    try {
      await invoiceService.create({
        customerEmail: form.customerEmail,
        customer: form.customer,
        shipment: form.shipment,
        items: form.items,
        lines: form.lines,
        dueDate: form.dueDate,
        notes: form.notes,
      });
      enqueueSnackbar("Invoice generated successfully", { variant: "success" });
      setInvoiceOpen(false);
      loadOne(id);
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to generate invoice",
        { variant: "error" },
      );
    } finally {
      setCreatingInvoice(false);
    }
  };

  const downloadDocument = async (doc) => {
    setDownloadingDocId(doc._id);
    try {
      const { data } = await documentService.download(doc._id);
      window.open(data.data.url, "_blank");
    } catch (err) {
      console.error(err);
    } finally {
      setDownloadingDocId(null);
    }
  };

  const viewDocument = async (doc) => {
    setViewingDocId(doc._id);
    try {
      const { data } = await documentService.view(doc._id);
      window.open(data.data.url, "_blank");
    } catch (err) {
      console.error(err);
    } finally {
      setViewingDocId(null);
    }
  };

  // Admin can upload a document (invoice or other) directly onto any
  // shipment, same presign/finalize flow CustomerUpload.jsx uses — the
  // backend already allows this for any role (see documentRoutes.js),
  // this just adds the UI for it here.
  const handleUploadDocument = async (e, type) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file name later
    if (!file) return;

    setUploadingDocType(type);
    try {
      await documentService.upload(id, [file], type);
      enqueueSnackbar(`${type === "invoice" ? "Invoice" : "Document"} uploaded`, { variant: "success" });
      loadOne(id);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to upload document", { variant: "error" });
    } finally {
      setUploadingDocType(null);
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Remove "${doc.originalName}"? This cannot be undone.`)) return;
    setDeletingDocId(doc._id);
    try {
      await documentService.delete(doc._id);
      enqueueSnackbar("Document removed", { variant: "success" });
      loadOne(id);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to remove document", { variant: "error" });
    } finally {
      setDeletingDocId(null);
    }
  };

  if (loading && !shipment) return <SkeletonLoader rows={8} />;
  if (!shipment) return null;

  const receivedCount =
    shipment.items?.filter((i) => i.itemStatus && i.itemStatus !== itemPendingKey).length || 0;
  const totalItems = shipment.items?.length || 0;

  // Delivered items sink to the bottom of the Items tab — nothing left to
  // do on them, so the ones still in progress stay front and center. A
  // stable sort (native Array#sort in every modern JS engine) preserves
  // each group's own original relative order, just partitioning delivered
  // vs. not.
  const displayItems = itemDeliveredKey
    ? [...(shipment.items || [])].sort(
        (a, b) => (a.itemStatus === itemDeliveredKey ? 1 : 0) - (b.itemStatus === itemDeliveredKey ? 1 : 0),
      )
    : shipment.items || [];

  const renderTrackingEvents = (events = []) => {
    if (!events.length) {
      return (
        <Typography variant="caption" color="text.secondary">
          No tracking updates yet
        </Typography>
      );
    }

    return events.slice(0, 5).map((e, idx) => (
      <Box key={idx} sx={{ mb: 1.5 }}>
        {/* description is the human-readable label from DHL */}
        <Typography variant="body2" fontWeight={600}>
          {e.description || "Update"}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}
        </Typography>

        {/* location is now a plain city string, not a nested object */}
        {e.location && (
          <Typography variant="caption" display="block">
            📍 {e.location}
          </Typography>
        )}
      </Box>
    ));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          size="small"
          onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/admin/shipments"))}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h4" fontWeight={700}>
              {shipment.packageDescription || shipment.vendor?.name}
            </Typography>
            <StatusChip status={shipment.status} domain="shipment" />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {shipment.trackingNumber} · {shipment.customer?.firstName}{" "}
            {shipment.customer?.lastName} · {shipment.vendor?.name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={() => navigate(`/admin/shipments/${id}/edit`)}
          >
            Edit
          </Button>
          {/* Assign to sailing — item-level: shows whenever at least one
              item is received, not already on a sailing, and not part of a
              consolidation. The dialog itself lets the admin pick which
              specific items go on which sailing. */}
          {shipment.items?.some(
            (it) => it.itemStatus === itemReceivedKey && !it.sailingSchedule && !it.consolidation,
          ) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DirectionsBoat />}
                onClick={() => setAssignSailingOpen(true)}
              >
                Assign to Sailing
              </Button>
            )}
          {shipment.items?.some(isItemConsolidationEligible) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CallMerge />}
              onClick={() => setConsolidationPicker({ mode: "shipment" })}
            >
              Consolidate
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={handleSyncAllItems}
            disabled={syncingAll}
          >
            {syncingAll ? "Updating..." : "Update Carrier Tracking Status"}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<SwapHoriz />}
            onClick={() => setStatusOpen(true)}
          >
            Update Status
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<Delete />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Tracker */}
      <Card>
        <CardContent sx={{ pt: 3, pb: 3 }}>
          <ShipmentTracker status={shipment.status} />
          {totalItems > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              mt={2}
            >
              📦 {receivedCount} of {totalItems} item
              {totalItems !== 1 ? "s" : ""} received
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Main grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 300px" },
          gap: 2.5,
        }}
      >
        {/* Left — tabs */}
        <Card>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              "& .MuiTab-root": {
                fontSize: 13,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              },
            }}
          >
            <Tab label="Details" />
            <Tab label="Items" />
            <Tab label="Status History" />
            <Tab label="Customs" />
            <Tab label="Documents" />
            <Tab label="Invoice" />
            <Tab label="Edit History" />
          </Tabs>

          {/* Details */}
          {tab === 0 && (
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRight: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  mb={1.5}
                  display="block"
                >
                  Customer
                </Typography>
                {[
                  {
                    label: "Name",
                    value: `${shipment.customer?.firstName} ${shipment.customer?.lastName}`,
                  },
                  { label: "Email", value: shipment.customer?.email },
                  { label: "Phone", value: shipment.customer?.phone || "—" },
                ].map((r) => (
                  <Box
                    key={r.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.875,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {r.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {r.value}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  mb={1.5}
                  display="block"
                >
                  Vendor
                </Typography>
                {[
                  { label: "Store", value: shipment.vendor?.name },
                  { label: "Country", value: shipment.vendor?.country },
                  { label: "Order #", value: shipment.orderNumber || "—" },
                  {
                    label: "Carrier #",
                    value: shipment.trackingNumber || "—",
                  },
                ].map((r) => (
                  <Box
                    key={r.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.875,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {r.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {r.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ p: 2.5 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  mb={1.5}
                  display="block"
                >
                  Shipment
                </Typography>
                {[
                  {
                    label: "Invoice Total",
                    value:
                      shipment.invoiceCurrency && shipment.invoiceCurrency !== "USD"
                        ? `${formatUsd(shipment.invoiceTotal)} (${shipment.invoiceTotalOriginal} ${shipment.invoiceCurrency})`
                        : formatUsd(shipment.invoiceTotal),
                  },
                  {
                    label: "Total Value (from items)",
                    value: (() => {
                      if (!shipment.invoiceCurrency) return formatUsd(shipment.declaredValue);
                      const originalTotal = (shipment.items || []).reduce(
                        (sum, it) => sum + (it.unitValueOriginal ?? 0) * (it.quantity || 1),
                        0,
                      );
                      return `${formatUsd(shipment.declaredValue)} (${originalTotal.toFixed(2)} ${shipment.invoiceCurrency})`;
                    })(),
                  },
                  {
                    label: "Freight Charge",
                    value: shipment.freightCharge
                      ? `$${shipment.freightCharge}`
                      : "—",
                  },
                  {
                    label: "Duty Amount",
                    value: shipment.dutyAmount
                      ? `$${shipment.dutyAmount}`
                      : "—",
                  },
                  {
                    label: "Arrival Date",
                    value: shipment.estimatedArrivalDate
                      ? dayjs(shipment.estimatedArrivalDate).format(
                        "MMM D, YYYY",
                      )
                      : "N/A",
                  },
                ].map((r) => (
                  <Box
                    key={r.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.875,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {r.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {r.value}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  mb={1.5}
                  display="block"
                >
                  Shipment Fees (whole shipment, admin-only)
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <TextField
                    size="small"
                    label="Delivery Fee ($)"
                    type="number"
                    value={feeDraft?.deliveryFee ?? shipment.deliveryFee ?? ""}
                    onChange={(e) =>
                      setFeeDraft((p) => ({
                        deliveryFee: shipment.deliveryFee ?? "",
                        handlingFee: shipment.handlingFee ?? "",
                        storageFee: shipment.storageFee ?? "",
                        ...p,
                        deliveryFee: e.target.value,
                      }))
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  <TextField
                    size="small"
                    label="Handling Fee ($)"
                    type="number"
                    value={feeDraft?.handlingFee ?? shipment.handlingFee ?? ""}
                    onChange={(e) =>
                      setFeeDraft((p) => ({
                        deliveryFee: shipment.deliveryFee ?? "",
                        handlingFee: shipment.handlingFee ?? "",
                        storageFee: shipment.storageFee ?? "",
                        ...p,
                        handlingFee: e.target.value,
                      }))
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  <TextField
                    size="small"
                    label="Storage Fee ($)"
                    type="number"
                    value={feeDraft?.storageFee ?? shipment.storageFee ?? ""}
                    onChange={(e) =>
                      setFeeDraft((p) => ({
                        deliveryFee: shipment.deliveryFee ?? "",
                        handlingFee: shipment.handlingFee ?? "",
                        storageFee: shipment.storageFee ?? "",
                        ...p,
                        storageFee: e.target.value,
                      }))
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={saveFees}
                  disabled={!feeDraft || savingFees}
                  sx={{ mb: 1 }}
                >
                  {savingFees ? <CircularProgress size={16} /> : "Save Fees"}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  These fees apply once to the whole shipment (not per item) and are included in "Total Value (from items)" above. Hidden from the customer.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                  sx={{ display: "block", mt: 1.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!shipment.deliverySelected}
                      onChange={toggleDeliverySelected}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Home delivery selected (used by invoice charge calculator)
                    </Typography>
                  }
                />
              </Box>
            </Box>
          )}

          {tab === 1 && (
            <Box
              sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {shipment.items?.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, alignSelf: "flex-end" }}>
                  <Button
                    size="small"
                    onClick={() => setExpandedItems(new Set(shipment.items.map((it) => it._id)))}
                  >
                    Expand All
                  </Button>
                  <Button size="small" onClick={() => setExpandedItems(new Set())}>
                    Collapse All
                  </Button>
                </Box>
              )}
              {displayItems.length > 0 ? (
                displayItems.map((item, i) => {
                  const isExpanded = expandedItems.has(item._id);
                  return (
                  <Card key={item._id || i} id={`shipment-item-${item._id}`} variant="outlined">
                    <CardContent>
                      {/* Tracking timeline — above the title, always visible */}
                      <Box sx={{ px: { xs: 0, sm: 1 }, mb: 2 }}>
                        <ShipmentTracker status={item.itemStatus} domain="item" />
                      </Box>

                      {/* Title + qty + expand arrow */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                          <Typography fontWeight={700} noWrap>
                            {item.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            Qty: {item.quantity}
                          </Typography>
                          <StatusChip status={item.itemStatus} domain="item" />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => toggleItemExpanded(item._id)}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUp fontSize="small" />
                          ) : (
                            <KeyboardArrowDown fontSize="small" />
                          )}
                        </IconButton>
                      </Box>

                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 2 }}>
                          {/* Actions */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setItemStatusDialogItem(item)}
                              disabled={updatingItemStatusId === item._id}
                            >
                              {updatingItemStatusId === item._id ? (
                                <CircularProgress size={18} />
                              ) : (
                                "Update Status"
                              )}
                            </Button>

                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSyncItem(item._id)}
                              disabled={
                                syncingItemId === item._id || !item.trackingNumber
                              }
                            >
                              {syncingItemId === item._id ? (
                                <CircularProgress size={18} />
                              ) : (
                                "Update Tracking Status"
                              )}
                            </Button>

                            {item.tracking?.delivered && (
                              <Chip
                                label="Delivered"
                                color="success"
                                size="small"
                              />
                            )}

                            {item.sailingSchedule && (
                              <Chip
                                icon={<DirectionsBoat sx={{ fontSize: 14 }} />}
                                label={item.sailingSchedule.sailingNumber || "Sailing"}
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  navigate(`/admin/sailing/${item.sailingSchedule.sailingNumber || item.sailingSchedule}`)
                                }
                              />
                            )}

                            {item.consolidation ? (
                              <Chip
                                icon={<CallMerge sx={{ fontSize: 14 }} />}
                                label="In Consolidation"
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              isItemConsolidationEligible(item) && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<CallMerge sx={{ fontSize: 14 }} />}
                                  onClick={() =>
                                    setConsolidationPicker({ mode: "item", itemId: item._id })
                                  }
                                >
                                  Add to Consolidation
                                </Button>
                              )
                            )}
                          </Box>

                          {/* Basic info */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              mb: 2,
                            }}
                          >
                            <Typography variant="body2">
                              Carrier: <b>{shipment.carrier || "—"}</b>
                            </Typography>

                            <Typography variant="body2">
                              Tracking:{" "}
                              <b style={{ fontFamily: "monospace" }}>
                                {item.trackingNumber || "—"}
                              </b>
                            </Typography>

                            <Typography variant="body2">
                              D/R:{" "}
                              <b style={{ fontFamily: "monospace" }}>
                                {item.dockReceiptNumber || "—"}
                              </b>
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                lg: "1fr 1fr",
                              },
                              gap: 1.5,
                              mb: 1,
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: "action.hover",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <TextField
                              size="small"
                              label="Price per item ($)"
                              type="number"
                              value={itemMeasurements[item._id]?.unitValue ?? item.unitValue ?? ""}
                              onChange={(e) =>
                                setItemMeasurements((p) => ({
                                  ...p,
                                  [item._id]: {
                                    ...p[item._id],
                                    unitValue: e.target.value,
                                  },
                                }))
                              }
                              inputProps={{ min: 0, step: 0.01 }}
                              fullWidth
                            />

                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => saveItemMeasurements(item._id)}
                              disabled={savingMeasurementsId === item._id}
                              sx={{
                                height: 40,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {savingMeasurementsId === item._id ? (
                                <CircularProgress size={16} />
                              ) : (
                                "Save"
                              )}
                            </Button>
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                            Total value:{" "}
                            <b>
                              ${((item.quantity || 1) * (item.unitValue || 0)).toFixed(2)}
                              {shipment.invoiceCurrency && item.unitValueOriginal != null
                                ? ` (${((item.quantity || 1) * item.unitValueOriginal).toFixed(2)} ${shipment.invoiceCurrency})`
                                : ""}
                            </b>{" "}
                            (qty {item.quantity || 1} × price) — the shipment's "Total Value (from items)" is the sum of these, plus any shipment-level fees.
                          </Typography>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                lg: "1fr 1fr 1fr",
                              },
                              gap: 1.5,
                              mb: 1,
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: "action.hover",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <TextField
                              size="small"
                              label="Weight (lb)"
                              type="number"
                              value={
                                weightCbfDraft[item._id]?.weight ?? item.weight ?? ""
                              }
                              onChange={(e) =>
                                setWeightCbfDraft((p) => ({
                                  ...p,
                                  [item._id]: { ...p[item._id], weight: e.target.value },
                                }))
                              }
                              inputProps={{ min: 0, step: 0.01 }}
                              fullWidth
                            />
                            <TextField
                              size="small"
                              label="CBF"
                              type="number"
                              value={weightCbfDraft[item._id]?.cbf ?? item.cbf ?? ""}
                              onChange={(e) =>
                                setWeightCbfDraft((p) => ({
                                  ...p,
                                  [item._id]: { ...p[item._id], cbf: e.target.value },
                                }))
                              }
                              inputProps={{ min: 0, step: 0.001 }}
                              fullWidth
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => saveWeightCbf(item._id)}
                              disabled={savingWeightCbfId === item._id}
                              sx={{ height: 40, whiteSpace: "nowrap" }}
                            >
                              {savingWeightCbfId === item._id ? (
                                <CircularProgress size={16} />
                              ) : (
                                "Save"
                              )}
                            </Button>
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                            {item.weightIsManual || item.cbfIsManual
                              ? "Manually overridden — clear both fields and Save to return to the auto-computed split across this item's dock receipt."
                              : "Auto-computed as this item's quantity-weighted share of its dock receipt's total weight/CBF. Enter a value to override just this item."}
                          </Typography>

                          {/* Tracking status */}

                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Carrier Status:
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 0.5,
                              }}
                            >
                              <Typography fontWeight={600}>
                                {item.tracking?.trackingStatus === "Pending" ? "No Status Update" : item.tracking?.trackingStatus}
                              </Typography>
                              {item.tracking?.trackingStatusCode && (
                                <Chip
                                  label={item.tracking.trackingStatusCode}
                                  size="small"
                                  color={
                                    item.tracking.trackingStatusCode === "delivered"
                                      ? "success"
                                      : item.tracking.trackingStatusCode === "transit"
                                        ? "info"
                                        : item.tracking.trackingStatusCode === "exception"
                                          ? "error"
                                          : item.tracking.trackingStatusCode === "pending"
                                            ? "default"
                                            : "default"
                                  }
                                  variant="outlined"
                                  sx={{ fontSize: 10, height: 20, textTransform: "capitalize" }}
                                />
                              )}
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                mt: 0.5,
                              }}
                            >
                              {item.tracking?.lastTrackingUpdate && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Last updated:{" "}
                                  {new Date(
                                    item.tracking.lastTrackingUpdate,
                                  ).toLocaleString()}
                                </Typography>
                              )}

                              {/* DHL product / weight metadata if available */}
                              {item.tracking?.product && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Service: {item.tracking.product}
                                </Typography>
                              )}
                              {item.tracking?.weight?.value && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Weight: {item.tracking.weight.value}{" "}
                                  {item.tracking.weight.unitText || "kg"}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          {/* Tracking Events */}
                          <Typography variant="caption" color="text.secondary">
                            Latest Tracking Updates
                          </Typography>

                          <Box sx={{ mt: 1 }}>
                            {renderTrackingEvents(item.tracking?.trackingEvents)}
                          </Box>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                  );
                })
              ) : (
                <Typography color="text.secondary">No items added.</Typography>
              )}
            </Box>
          )}

          {/* Status History */}
          {tab === 2 && (
            <ShipmentTimeline statusHistory={shipment.statusHistory} />
          )}

          {/* Customs */}
          {tab === 3 && (
            <Box sx={{ p: 2.5 }}>
              {[
                {
                  label: "Declaration Submitted",
                  value: shipment.customsDeclarationSubmitted ? "Yes" : "No",
                },
                {
                  label: "Customs Cleared At",
                  value: shipment.customsClearedAt
                    ? dayjs(shipment.customsClearedAt).format("MMM D, YYYY")
                    : "Pending",
                },
                { label: "Customs Notes", value: shipment.customsNotes || "—" },
              ].map((r) => (
                <Box
                  key={r.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {r.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {r.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {tab === 4 && (
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
                <input
                  ref={invoiceFileInputRef}
                  type="file"
                  hidden
                  accept="image/*,application/pdf"
                  onChange={(e) => handleUploadDocument(e, "invoice")}
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={uploadingDocType === "invoice" ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
                  onClick={() => invoiceFileInputRef.current?.click()}
                  disabled={!!uploadingDocType}
                >
                  Upload Invoice
                </Button>

                <input
                  ref={otherFileInputRef}
                  type="file"
                  hidden
                  accept="image/*,application/pdf"
                  onChange={(e) => handleUploadDocument(e, "other")}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={uploadingDocType === "other" ? <CircularProgress size={16} /> : <UploadFile />}
                  onClick={() => otherFileInputRef.current?.click()}
                  disabled={!!uploadingDocType}
                >
                  Upload Other Document
                </Button>
              </Box>

              {shipment.documents?.length > 0 ? (
                <Table size="small">
                  <TableBody>
                    <TableRow
                      sx={{
                        "& td": {
                          fontWeight: 600,
                          color: "text.secondary",
                          fontSize: 11,
                          textTransform: "uppercase",
                        },
                      }}
                    >
                      <TableCell>File</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Uploaded</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                    {shipment.documents.map((doc) => (
                      <TableRow key={doc._id}>
                        <TableCell>{doc.originalName}</TableCell>
                        <TableCell>
                          {doc.type === "invoice"
                            ? "Purchase Invoice"
                            : "Other Document"}
                        </TableCell>
                        <TableCell>
                          {dayjs(doc.createdAt).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "inline-flex", gap: 1 }}>
                            {(doc.mimeType?.startsWith("image/") || doc.mimeType === "application/pdf") && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Visibility />}
                                onClick={() => viewDocument(doc)}
                                disabled={viewingDocId === doc._id}
                              >
                                View
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Download />}
                              onClick={() => downloadDocument(doc)}
                              disabled={downloadingDocId === doc._id}
                            >
                              Download
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDocument(doc)}
                              disabled={deletingDocId === doc._id}
                            >
                              {deletingDocId === doc._id ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No documents uploaded for this shipment yet.
                </Typography>
              )}
            </Box>
          )}

          {tab === 5 && (
            <Box sx={{ p: 2.5, display: "grid", gap: 2 }}>
              {shipment.invoice ? (
                <Box>
                  <Typography variant="h6" mb={1}>
                    Invoice details
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Invoice #
                      </Typography>
                      <Typography fontWeight={700}>
                        {shipment.invoice.invoiceNumber}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status
                      </Typography>
                      <Typography fontWeight={700}>
                        {shipment.invoice.status}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography fontWeight={700}>
                        ${shipment.invoice.totalAmount?.toFixed(2) || "0.00"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Due date
                      </Typography>
                      <Typography fontWeight={700}>
                        {shipment.invoice.dueDate
                          ? dayjs(shipment.invoice.dueDate).format(
                            "MMM D, YYYY",
                          )
                          : "—"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Alert severity="info">
                  Generate an invoice for this shipment to notify the customer.
                </Alert>
              )}
              <Button
                variant="contained"
                onClick={() => setInvoiceOpen(true)}
                disabled={!!shipment.invoice}
              >
                {shipment.invoice
                  ? "Invoice already generated"
                  : "Generate Invoice"}
              </Button>
            </Box>
          )}

          {/* Edit History — admin-only; never shown on the customer-facing
              shipment detail page. */}
          {tab === 6 && (
            <Box sx={{ p: 2.5 }}>
              {loadingActivityLog ? (
                <SkeletonLoader rows={4} />
              ) : activityLog.length === 0 ? (
                <Alert severity="info">No admin edits recorded for this shipment yet.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Previous</TableCell>
                      <TableCell />
                      <TableCell>New</TableCell>
                      <TableCell>Changed By</TableCell>
                      <TableCell>When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityLog.map((entry) => (
                      <TableRow key={entry._id}>
                        <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>{entry.label}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                          {entry.previousValue === null || entry.previousValue === "" ? "—" : String(entry.previousValue)}
                        </TableCell>
                        <TableCell sx={{ px: 0 }}>
                          <ArrowForward sx={{ fontSize: 14, color: "text.disabled" }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
                          {entry.newValue === null || entry.newValue === "" ? "—" : String(entry.newValue)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                          {entry.changedBy ? (
                            `${entry.changedBy.firstName} ${entry.changedBy.lastName}`
                          ) : (
                            <Chip label="System" size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                          {dayjs(entry.changedAt).format("MMM D, YYYY h:mm A")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </Card>

        {/* Right sidebar */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Internal notes */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={13}>
                  Internal Notes
                </Typography>
              }
              avatar={<Notes sx={{ color: "text.secondary", fontSize: 18 }} />}
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add internal notes visible only to staff…"
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <Button
                size="small"
                variant="contained"
                fullWidth
                onClick={saveNote}
                disabled={savingNote}
              >
                {savingNote ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Save Note"
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Quick info */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={13}>
                  Quick Info
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              {[
                {
                  label: "Created",
                  value: dayjs(shipment.createdAt).format("MMM D, YYYY"),
                },
                {
                  label: "Last Updated",
                  value:
                    dayjs(shipment.updatedAt).fromNow?.() ||
                    dayjs(shipment.updatedAt).format("MMM D"),
                },
                {
                  label: "Documents",
                  value: `${shipment.documents?.length || 0} uploaded`,
                },
              ].map((r) => (
                <Box
                  key={r.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.875,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {r.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {r.value}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <InvoiceDialog
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        onSubmit={handleCreateInvoice}
        loading={creatingInvoice}
        defaultEmail={shipment.customer?.email}
        shipment={shipment}
      />

      <StatusUpdateDialog
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        currentStatus={shipment.status}
        onSubmit={handleStatusUpdate}
        loading={updatingStatus}
      />

      <ItemStatusUpdateDialog
        open={!!itemStatusDialogItem}
        onClose={() => setItemStatusDialogItem(null)}
        currentStatus={itemStatusDialogItem?.itemStatus}
        itemDescription={itemStatusDialogItem?.description}
        onSubmit={handleItemStatusChange}
        loading={updatingItemStatusId === itemStatusDialogItem?._id}
      />

      <AssignSailingDialog
        open={assignSailingOpen}
        shipment={shipment}
        onClose={() => setAssignSailingOpen(false)}
        onAssigned={() => loadOne(id)}
      />

      <ConsolidationPickerDialog
        open={!!consolidationPicker}
        onClose={() => setConsolidationPicker(null)}
        mode={consolidationPicker?.mode}
        shipmentId={id}
        customerId={shipment.customer?._id}
        itemId={consolidationPicker?.itemId}
        onDone={() => loadOne(id)}
      />
    </Box>
  );
};

export default AdminShipmentDetail;
